import React from 'react';
import { theme } from '../config/theme';

/**
 * TimelineFlow — soft particle field suggesting time flow.
 * Extremely lightweight 2D canvas with throttled 30fps and IO pause.
 */
export default function TimelineFlow({ className = '' }) {
  const ref = React.useRef(null);
  const frame = React.useRef(0);
  const running = React.useRef(true);
  const particlesRef = React.useRef([]);

  React.useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w = canvas.clientWidth, h = canvas.clientHeight;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.scale(dpr, dpr);

    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // ~1.6x denser than previous settings
    const count = prefersReduced
      ? 128
      : Math.max(176, Math.min(352, Math.floor(w * h / 8750)));
    const particles = Array.from({ length: count }).map(() => createParticle(w, h));
    particlesRef.current = particles;

    let last = 0;
    function draw(t) {
      if (!running.current) return;
      frame.current = requestAnimationFrame(draw);
      if (t - last < 33) return; // ~30fps
      last = t;

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (const p of particles) {
        // velocity with slight drift to the right (perspective)
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.005;
        if (p.y > h + 20 || p.x < -20 || p.x > w + 20 || p.life <= 0) {
          // respawn anywhere in the block (not just from the top)
          Object.assign(p, createParticle(w, h, false));
        }

        const alpha = Math.max(0, Math.min(0.85, p.life * 0.85));
        ctx.fillStyle = theme.mode === 'light' ? `rgba(30, 102, 245, ${alpha})` : `rgba(96, 165, 250, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    frame.current = requestAnimationFrame(draw);

    const io = new IntersectionObserver((entries) => {
      running.current = entries[0]?.isIntersecting ?? true;
      if (running.current && !frame.current) frame.current = requestAnimationFrame(draw);
      if (!running.current && frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
    }, { threshold: 0.05 });
    io.observe(canvas);

    const ro = new ResizeObserver(() => {
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
      ctx.scale(dpr, dpr);
    });
    ro.observe(canvas);

    return () => { io.disconnect(); ro.disconnect(); if (frame.current) cancelAnimationFrame(frame.current); };
  }, []);

  return <canvas ref={ref} className={className} style={{ width: '100%', height: '100%', opacity: 0.6 }} aria-hidden />;
}

function createParticle(w, h, fromTop = false) {
  // spawn across full width; when recycled, start slightly above top edge
  const x = Math.random() * w;
  const y = fromTop ? -20 : Math.random() * h;
  const depth = Math.random();
  const size = 0.7 + depth * 1.9;
  const speed = 0.45 + depth * 0.9;
  return { x, y, vx: (Math.random() - 0.5) * 0.25 + 0.15, vy: speed, size, life: 0.7 + Math.random() * 0.6 };
}


